import { useCallback, useEffect, useState } from 'react';
import type { ExcalidrawData } from '../store/types.ts';

const EXCALIDRAW_STORAGE_PREFIX = 'sketchflow.excalidraw.';

function loadExcalidrawData(diagramId: string): ExcalidrawData | undefined {
  try {
    const raw = localStorage.getItem(EXCALIDRAW_STORAGE_PREFIX + diagramId);
    if (!raw) return undefined;
    return JSON.parse(raw) as ExcalidrawData;
  } catch {
    return undefined;
  }
}

function saveExcalidrawData(diagramId: string, data: ExcalidrawData) {
  try {
    localStorage.setItem(EXCALIDRAW_STORAGE_PREFIX + diagramId, JSON.stringify(data));
  } catch {
    // ignore storage errors
  }
}

export function useExcalidraw(diagramId: string | undefined) {
  const [excalidrawData, setExcalidrawData] = useState<ExcalidrawData | undefined>(() =>
    diagramId ? loadExcalidrawData(diagramId) : undefined,
  );
  const [generationKey, setGenerationKey] = useState(0);

  // Reload excalidraw data when diagram changes
  useEffect(() => {
    setExcalidrawData(diagramId ? loadExcalidrawData(diagramId) : undefined);
  }, [diagramId]);

  const handleExcalidrawChange = useCallback(
    (data: ExcalidrawData) => {
      setExcalidrawData(data);
      if (diagramId) saveExcalidrawData(diagramId, data);
    },
    [diagramId],
  );

  const setExcalidrawFromAI = useCallback(
    (data: ExcalidrawData) => {
      setExcalidrawData(data);
      setGenerationKey((k) => k + 1);
      if (diagramId) saveExcalidrawData(diagramId, data);
    },
    [diagramId],
  );

  const convertMermaidToExcalidraw = useCallback(
    async (mermaidCode: string) => {
      try {
        const { parseMermaidToExcalidraw } = await import('@excalidraw/mermaid-to-excalidraw');
        const { elements } = await parseMermaidToExcalidraw(mermaidCode);
        const data: ExcalidrawData = {
          elements: elements as unknown[],
          appState: {},
          files: {},
        };
        setExcalidrawData(data);
        if (diagramId) saveExcalidrawData(diagramId, data);
        return data;
      } catch (error) {
        console.error('Mermaid to Excalidraw conversion failed:', error);
        return undefined;
      }
    },
    [diagramId],
  );

  return { excalidrawData, generationKey, handleExcalidrawChange, setExcalidrawFromAI, convertMermaidToExcalidraw };
}
